import { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { moderationRpc, transformReview } from '../api';

interface UseReviewsOptions {
  search?: string;
  limit?: number;
  offset?: number;
}

export function useReviews(options: UseReviewsOptions = {}) {
  const { search, limit = 50, offset = 0 } = options;

  const query = useQuery({
    queryKey: ['admin', 'reviews', search, limit, offset],
    queryFn: async () => {
      const { data, error } = await moderationRpc.getAllReviews({
        p_search: search || undefined,
        p_limit: limit,
        p_offset: offset,
      });

      if (error) throw error;
      if (!data) throw new Error('No data returned');

      return data.map(transformReview);
    },
  });

  return {
    reviews: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useDeleteReview() {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteReview = useCallback(
    async (reviewId: string) => {
      setIsDeleting(true);
      try {
        const { error } = await moderationRpc.deleteReview({
          p_review_id: reviewId,
        });

        if (error) throw error;

        // Invalidate queries
        await queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
        await queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });

        return { success: true };
      } catch (err) {
        return { success: false, error: err as Error };
      } finally {
        setIsDeleting(false);
      }
    },
    [queryClient]
  );

  return { deleteReview, isDeleting };
}
